/**
 * Booking Widget Management Page
 * 
 * Manages public booking page settings for the practice
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Trash2, Copy, Check, ExternalLink, Settings } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

interface Widget {
  id: string;
  name: string;
  practice_id: string;
  created_at: string;
  expires_at: string | null;
  last_used_at: string | null;
  environment: string;
}

interface OnlineBookingSettings {
  online_booking_enabled: boolean;
  online_booking_advance_days: number;
  online_booking_min_notice_hours: number;
  default_appointment_duration_minutes: number;
  slug: string | null;
  city: string | null;
  state: string | null;
}

export default function BookingWidget() {
  const { user } = useAuth();
  const [widget, setWidget] = useState<Widget | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Online booking settings
  const [practiceId, setPracticeId] = useState<string | null>(null);
  const [bookingSettings, setBookingSettings] = useState<OnlineBookingSettings>({
    online_booking_enabled: false,
    online_booking_advance_days: 14,
    online_booking_min_notice_hours: 2,
    default_appointment_duration_minutes: 30,
    slug: null,
    city: null,
    state: null
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [copiedBookingUrl, setCopiedBookingUrl] = useState(false);

  useEffect(() => {
    loadWidget();
    // Also set practice ID from user if available
    if (user?.practice_id) {
      setPracticeId(user.practice_id);
    }
  }, [user?.practice_id]);

  // Load booking settings when we have a practice ID
  useEffect(() => {
    if (practiceId) {
      loadBookingSettings(practiceId);
    }
  }, [practiceId]);

  const loadBookingSettings = async (pId: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/practices/${pId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setBookingSettings({
          online_booking_enabled: data.online_booking_enabled || false,
          online_booking_advance_days: data.online_booking_advance_days || 14,
          online_booking_min_notice_hours: data.online_booking_min_notice_hours || 2,
          default_appointment_duration_minutes: data.default_appointment_duration_minutes || 30,
          slug: data.slug,
          city: data.city,
          state: data.state
        });
      }
    } catch (error) {
      console.error('Failed to load booking settings:', error);
    }
  };

  const saveBookingSettings = async () => {
    if (!practiceId) return;
    setSavingSettings(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/practices/${practiceId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          online_booking_enabled: bookingSettings.online_booking_enabled,
          online_booking_advance_days: bookingSettings.online_booking_advance_days,
          online_booking_min_notice_hours: bookingSettings.online_booking_min_notice_hours,
          default_appointment_duration_minutes: bookingSettings.default_appointment_duration_minutes
        }),
      });
      if (!response.ok) {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Failed to save booking settings:', error);
      alert('Failed to save settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const getPublicBookingUrl = () => {
    if (!bookingSettings.state || !bookingSettings.city || !bookingSettings.slug) {
      return null;
    }
    const baseUrl = window.location.origin;
    return `${baseUrl}/book/${bookingSettings.state.toLowerCase()}/${bookingSettings.city.toLowerCase().replace(/\s+/g, '-')}/${bookingSettings.slug}`;
  };

  const copyBookingUrl = () => {
    const url = getPublicBookingUrl();
    if (url) {
      navigator.clipboard.writeText(url);
      setCopiedBookingUrl(true);
      setTimeout(() => setCopiedBookingUrl(false), 2000);
    }
  };

  const loadWidget = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/booking-widgets`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.length > 0) {
          setWidget(data[0]);
          setPracticeId(data[0].practice_id);
        }
      }
    } catch (error) {
      console.error('Failed to load widget:', error);
    } finally {
      setLoading(false);
    }
  };

  const createWidget = async () => {
    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/booking-widgets`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: 'Website Booking',
          environment: 'production'
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        setWidget(data);
        setPracticeId(data.practice_id);
      } else {
        const errorData = await response.json();
        console.error('Failed to create widget:', errorData);
        alert('Failed to create widget: ' + (errorData.detail || 'Unknown error'));
      }
    } catch (error) {
      console.error('Failed to create widget:', error);
    } finally {
      setCreating(false);
    }
  };

  const deleteWidget = async () => {
    if (!widget) return;
    
    setIsDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/v1/booking-widgets/${widget.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        setWidget(null);
        setShowDeleteConfirm(false);
      }
    } catch (error) {
      console.error('Failed to delete widget:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-gray-600">Loading...</div>;
  }

  // Show create screen if no widget
  if (!widget) {
    return (
      <div className="pt-8 pb-12">
        {/* Text and CTA */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-blue-600" />
          </div>
          <h3 className="text-3xl font-semibold text-gray-900 mb-3">Online Booking</h3>
          <p className="text-gray-600 mb-8 text-lg">
            Let pet owners book appointments 24/7 directly from your website. Show real-time availability and accept bookings even when your phone lines are closed.
          </p>
          <button
            onClick={createWidget}
            disabled={creating}
            className="px-8 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium text-lg"
          >
            {creating ? 'Enabling...' : 'Enable online booking'}
          </button>
        </div>
        
        {/* Hero Image - Same style as homepage */}
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-gray-200">
            <img 
              src="/booking.png" 
              alt="Online booking page preview" 
              className="w-full h-auto"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Card */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Globe className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Online Booking</h3>
              <p className="text-sm text-gray-500">Accept appointments from your booking page</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-3 py-1 rounded-full flex items-center space-x-2 bg-green-100">
              <div className="w-2 h-2 rounded-full bg-green-600"></div>
              <span className="text-sm font-medium text-green-700">Active</span>
            </div>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
              title="Delete booking widget"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Public Booking Page Settings */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h4 className="text-lg font-medium text-gray-900">Public Booking Page</h4>
            <p className="text-sm text-gray-500">Allow customers to book directly from helppet.ai</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={bookingSettings.online_booking_enabled}
              onChange={(e) => {
                setBookingSettings(prev => ({ ...prev, online_booking_enabled: e.target.checked }));
              }}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
          </label>
        </div>

        {bookingSettings.online_booking_enabled && (
          <>
            {/* Booking URL */}
            {getPublicBookingUrl() ? (
              <div className="mb-4 flex items-center gap-2">
                <code className="flex-1 text-sm bg-gray-50 px-3 py-2 rounded border border-green-200 text-green-700 truncate">
                  {getPublicBookingUrl()}
                </code>
                <button
                  onClick={copyBookingUrl}
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Copy URL"
                >
                  {copiedBookingUrl ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                </button>
                <a
                  href={getPublicBookingUrl()!}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            ) : (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between">
                <p className="text-sm text-amber-800">
                  Add your city and state to generate your booking URL.
                </p>
                <Link
                  to="/settings/practice"
                  className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap"
                >
                  <Settings className="w-4 h-4" />
                  Add in Practice Settings
                </Link>
              </div>
            )}

            {/* Booking Settings */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Advance Booking (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={bookingSettings.online_booking_advance_days}
                  onChange={(e) => setBookingSettings(prev => ({ 
                    ...prev, 
                    online_booking_advance_days: parseInt(e.target.value) || 14 
                  }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">How far ahead customers can book</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Notice (hours)
                </label>
                <input
                  type="number"
                  min="0"
                  max="72"
                  value={bookingSettings.online_booking_min_notice_hours}
                  onChange={(e) => setBookingSettings(prev => ({ 
                    ...prev, 
                    online_booking_min_notice_hours: parseInt(e.target.value) || 2 
                  }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">Minimum hours before appointment</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Appointment Duration
                </label>
                <select
                  value={bookingSettings.default_appointment_duration_minutes}
                  onChange={(e) => setBookingSettings(prev => ({ 
                    ...prev, 
                    default_appointment_duration_minutes: parseInt(e.target.value) || 30 
                  }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">Default length of appointments</p>
              </div>
            </div>

            <button
              onClick={saveBookingSettings}
              disabled={savingSettings}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {savingSettings ? 'Saving...' : 'Save Settings'}
            </button>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Delete Booking Widget</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete this booking widget? This will disable online bookings.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                disabled={isDeleting}
              >
                Cancel
              </button>
              <button
                onClick={deleteWidget}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
